import { CONVERSATION_PRIORITY_ORDER } from 'shared/constants/messages';
import { findConversationMessageIdentityMatchIndex } from 'dashboard/helper/conversationMessageIdentity';

export const findPendingMessageIndex = (chat, message) =>
  findConversationMessageIdentityMatchIndex(chat?.messages, message);

export const filterByStatus = (chatStatus, filterStatus) =>
  filterStatus === 'all' ? true : chatStatus === filterStatus;

export const filterByInbox = (shouldFilter, inboxId, chatInboxId) => {
  const isOnInbox = Number(inboxId) === chatInboxId;
  return inboxId ? isOnInbox && shouldFilter : shouldFilter;
};

export const filterByLabel = (shouldFilter, labels, chatLabels) => {
  const isOnLabel = labels.every((label) => chatLabels.includes(label));
  return labels.length ? isOnLabel && shouldFilter : shouldFilter;
};
export const filterByUnattended = (
  shouldFilter,
  conversationType,
  firstReplyOn,
  waitingSince
) => {
  return conversationType === 'unattended'
    ? (!firstReplyOn || !!waitingSince) && shouldFilter
    : shouldFilter;
};

export const applyPageFilters = (conversation, filters) => {
  const { inboxId, status, labels = [], conversationType } = filters;
  const {
    status: chatStatus,
    inbox_id: chatInboxId,
    labels: chatLabels = [],
    first_reply_created_at: firstReplyOn,
    waiting_since: waitingSince,
  } = conversation;

  let shouldFilter = filterByStatus(chatStatus, status);
  shouldFilter = filterByInbox(shouldFilter, inboxId, chatInboxId);
  shouldFilter = filterByLabel(shouldFilter, labels, chatLabels);
  shouldFilter = filterByUnattended(
    shouldFilter,
    conversationType,
    firstReplyOn,
    waitingSince
  );

  return shouldFilter;
};

const SORT_OPTIONS = {
  last_activity_at_asc: ['sortOnLastActivityAt', 'asc'],
  last_activity_at_desc: ['sortOnLastActivityAt', 'desc'],
  created_at_asc: ['sortOnCreatedAt', 'asc'],
  created_at_desc: ['sortOnCreatedAt', 'desc'],
  priority_asc: ['sortOnPriority', 'asc'],
  priority_desc: ['sortOnPriority', 'desc'],
  waiting_since_asc: ['sortOnWaitingSince', 'asc'],
  waiting_since_desc: ['sortOnWaitingSince', 'desc'],
  // Mixed list sort: email uses last incoming; chat channels use last message.
  last_incoming_email_asc: ['sortOnLastIncomingEmail', 'asc'],
  last_incoming_email_desc: ['sortOnLastIncomingEmail', 'desc'],
  // Non-email inbox sort (WhatsApp): order by last real message (in OR out)
  last_message_at_asc: ['sortOnLastMessageAt', 'asc'],
  last_message_at_desc: ['sortOnLastMessageAt', 'desc'],
  priority_urgency: ['sortOnPriorityUrgency', 'desc'],
};
const sortAscending = (valueA, valueB) => valueA - valueB;
const sortDescending = (valueA, valueB) => valueB - valueA;

const getSortOrderFunction = (sortOrder) =>
  sortOrder === 'asc' ? sortAscending : sortDescending;

const toNumericTimestamp = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const EMAIL_CHANNEL = 'Channel::Email';

// Ordine canali per il sort priority_urgency (valore più basso = prima)
const CHANNEL_ORDER = {
  'Channel::Email': 1,
  'Channel::Whatsapp': 2,
  'Channel::Api': 3,
};
const CHANNEL_ORDER_DEFAULT = 4;

const getChannelOrder = (conversation) => {
  const channel = conversation.meta?.channel || conversation.channel_type || '';
  return CHANNEL_ORDER[channel] || CHANNEL_ORDER_DEFAULT;
};

const isEmailChannelConversation = (conversation) => {
  if (!conversation) {
    return false;
  }

  const channelHints = [
    conversation.channel,
    conversation.channel_type,
    conversation.meta?.channel,
    conversation.meta?.inbox?.channel_type,
  ].filter(Boolean);

  return channelHints.includes(EMAIL_CHANNEL);
};

const resolveLastIncomingAt = (conversation) => {
  const {
    last_incoming_at: lastIncomingAt,
    last_message_at: lastMessageAt,
    last_non_activity_message: lastNonActivityMessage,
    messages = [],
    timestamp,
    last_activity_at: lastActivityAt,
    created_at: createdAt,
  } = conversation || {};

  const treatAsEmail = isEmailChannelConversation(conversation);

  let resolved = null;

  if (treatAsEmail) {
    resolved = toNumericTimestamp(lastIncomingAt);
    if (resolved !== null) {
      return resolved;
    }
  }

  if (!treatAsEmail) {
    resolved = toNumericTimestamp(lastMessageAt);
    if (resolved !== null) {
      return resolved;
    }

    resolved = toNumericTimestamp(lastNonActivityMessage?.created_at);
    if (resolved !== null) {
      return resolved;
    }
  }

  if (Array.isArray(messages) && messages.length) {
    for (let idx = messages.length - 1; idx >= 0; idx -= 1) {
      const message = messages[idx];
      if (message) {
        const messageType =
          typeof message.message_type === 'string'
            ? message.message_type.toLowerCase()
            : message.message_type;

        const isIncoming =
          messageType === 0 ||
          messageType === 'incoming' ||
          message.message_type_before_type_cast === 0;

        const isOutgoing =
          messageType === 1 ||
          messageType === 'outgoing' ||
          message.message_type_before_type_cast === 1;

        if (isIncoming || (!treatAsEmail && isOutgoing)) {
          resolved = toNumericTimestamp(message.created_at);
          if (resolved !== null) {
            return resolved;
          }
        }
      }
    }
  }

  if (treatAsEmail) {
    resolved = toNumericTimestamp(createdAt);
    if (resolved === null) {
      // Fallback to timestamp/lastActivity only if createdAt is missing for robustness.
      resolved =
        toNumericTimestamp(timestamp) || toNumericTimestamp(lastActivityAt);
    }
  } else {
    resolved =
      toNumericTimestamp(lastActivityAt) ||
      toNumericTimestamp(createdAt) ||
      toNumericTimestamp(timestamp);
  }

  // Ensure we always return a number so sort functions can operate safely
  return resolved !== null ? resolved : 0;
};

// Timestamp dell'ultimo messaggio reale (incoming O outgoing). Usa il campo
// last_message_at esposto dal server (MAX created_at in/out); in fallback il
// last_non_activity_message, poi i messaggi caricati, infine timestamp/created.
const resolveLastMessageAt = (conversation) => {
  const {
    last_message_at: lastMessageAt,
    last_non_activity_message: lastNonActivityMessage,
    messages = [],
    timestamp,
    last_activity_at: lastActivityAt,
    created_at: createdAt,
  } = conversation || {};

  let resolved = toNumericTimestamp(lastMessageAt);
  if (resolved !== null) {
    return resolved;
  }

  resolved = toNumericTimestamp(lastNonActivityMessage?.created_at);
  if (resolved !== null) {
    return resolved;
  }

  if (Array.isArray(messages) && messages.length) {
    for (let idx = messages.length - 1; idx >= 0; idx -= 1) {
      const message = messages[idx];
      if (message) {
        const messageType =
          typeof message.message_type === 'string'
            ? message.message_type.toLowerCase()
            : message.message_type;

        const isIncomingOrOutgoing =
          messageType === 0 ||
          messageType === 1 ||
          messageType === 'incoming' ||
          messageType === 'outgoing' ||
          message.message_type_before_type_cast === 0 ||
          message.message_type_before_type_cast === 1;

        if (isIncomingOrOutgoing) {
          resolved = toNumericTimestamp(message.created_at);
          if (resolved !== null) {
            return resolved;
          }
        }
      }
    }
  }

  resolved =
    toNumericTimestamp(timestamp) ||
    toNumericTimestamp(lastActivityAt) ||
    toNumericTimestamp(createdAt);

  return resolved !== null ? resolved : 0;
};

const sortConfig = {
  sortOnLastActivityAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.last_activity_at, b.last_activity_at),

  sortOnCreatedAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.created_at, b.created_at),

  sortOnPriority: (a, b, sortDirection) => {
    const DEFAULT_FOR_NULL = sortDirection === 'asc' ? 5 : 0;

    const p1 = CONVERSATION_PRIORITY_ORDER[a.priority] || DEFAULT_FOR_NULL;
    const p2 = CONVERSATION_PRIORITY_ORDER[b.priority] || DEFAULT_FOR_NULL;

    return getSortOrderFunction(sortDirection)(p1, p2);
  },

  sortOnWaitingSince: (a, b, sortDirection) => {
    const sortFunc = getSortOrderFunction(sortDirection);
    if (!a.waiting_since || !b.waiting_since) {
      if (!a.waiting_since && !b.waiting_since) {
        return sortFunc(a.created_at, b.created_at);
      }
      return sortFunc(a.waiting_since ? 0 : 1, b.waiting_since ? 0 : 1);
    }

    return sortFunc(a.waiting_since, b.waiting_since);
  },
  sortOnLastIncomingEmail: (a, b, sortDirection) => {
    const sortFunc = getSortOrderFunction(sortDirection);
    const timeA = resolveLastIncomingAt(a);
    const timeB = resolveLastIncomingAt(b);

    if (timeA === timeB) {
      // Fallback to created_at to keep order stable when timestamps match
      return sortConfig.sortOnCreatedAt(a, b, sortDirection);
    }

    return sortFunc(timeA, timeB);
  },
  sortOnLastMessageAt: (a, b, sortDirection) => {
    const sortFunc = getSortOrderFunction(sortDirection);
    const timeA = resolveLastMessageAt(a);
    const timeB = resolveLastMessageAt(b);

    if (timeA === timeB) {
      // Fallback to created_at to keep order stable when timestamps match
      return sortConfig.sortOnCreatedAt(a, b, sortDirection);
    }

    return sortFunc(timeA, timeB);
  },

  // Con priorità prima.
  // Dentro ogni gruppo: priority DESC → last incoming DESC → channel ASC.
  sortOnPriorityUrgency: (a, b) => {
    const aHas = a.priority != null;
    const bHas = b.priority != null;
    if (aHas !== bHas) return aHas ? -1 : 1;

    if (aHas && bHas) {
      const pA = CONVERSATION_PRIORITY_ORDER[a.priority] || 0;
      const pB = CONVERSATION_PRIORITY_ORDER[b.priority] || 0;
      if (pA !== pB) return pB - pA; // desc
    }

    const timeA = resolveLastIncomingAt(a);
    const timeB = resolveLastIncomingAt(b);
    if (timeA !== timeB) return timeB - timeA; // desc

    const chA = getChannelOrder(a);
    const chB = getChannelOrder(b);
    if (chA !== chB) return chA - chB; // asc

    return (b.id || 0) - (a.id || 0); // desc
  },
};

export const sortComparator = (a, b, sortKey) => {
  const [sortMethod, sortDirection] =
    SORT_OPTIONS[sortKey] || SORT_OPTIONS.last_activity_at_desc;

  return sortConfig[sortMethod](a, b, sortDirection);
};

// Client-side unread-first sort mirrors the backend CASE WHEN in conversation_finder.rb.
// Both must agree: unread = agent_last_seen_at is null/falsy → sorted first.
// This client sort keeps the list consistent between API refreshes and realtime updates.
export const sortComparatorWithUnreadFirst = (a, b, sortKey, unreadFirst) => {
  if (unreadFirst) {
    const aUnread = !a.agent_last_seen_at ? 0 : 1;
    const bUnread = !b.agent_last_seen_at ? 0 : 1;
    if (aUnread !== bUnread) return aUnread - bUnread;
  }
  return sortComparator(a, b, sortKey);
};
